/**
 * A HOC that renders a ModuleList for /hourofcode
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {staticUrl} from "@repo/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import InfoBlockGroup from "#marketing-content-shared-package/info-block-group.tsx";
import InfoBlock from "#marketing-content-shared-package/info-block.tsx";
import LastChance from "#marketing-content-shared-package/last-chance.tsx";
import List from "#marketing-content-shared-package/list.tsx";
import ScrollButton from "#marketing-content-shared-package/scroll-button.tsx";
import Testimonial from "#marketing-content-shared-package/testimonial.tsx";
import ModuleList from "#modules-package/module-list.tsx";
import type {Module} from "#modules-package/types.ts";

import Hero from "./hero.tsx";
import {withStrings} from "./locales/hour-of-code-page/load-strings.tsx";
import Media from "./media.tsx";

// A collection of all possible Hour of Code Landing Page modules
const hourOfCodeModules = {
    Hero,
    ScrollButton,
    List,
    InfoBlock,
    InfoBlockGroup,
    LastChance,
    Testimonial,
} as const;

const getModules = () => {
    const modules: Array<Module> = [];

    modules.push(
        {kind: "Hero"},
        {
            kind: "ScrollButton",
            // These two properties need to match the properties of the
            // module you want the scroll button to scroll to.
            copy: t`Jump to: Many ways to do Hour of Code`,
            scrollToSelector: '[data-scroll-id="tutorial-options"]',
            alternateBackground: true,
        },
        {
            kind: "List",
            headline: t`We have many ways to do Hour of Code at Khan Academy.`,
            scrollId: "tutorial-options",
            listItems: [
                {
                    kind: "ListItem",
                    headline: t`Hour of Drawing with Code`,
                    copy: t`Learn how to program drawings using JavaScript by designing your very own snowman.`,
                    link: {
                        href: "/computing/hour-of-code/hour-of-code-lessons/hour-of-drawing-code/v/welcome-hour-of-code",
                        copy: t`Start drawing with code`,
                    },
                    illustration: "HourOfDrawing",
                },
                {
                    kind: "ListItem",
                    headline: t`Hour of Webpages`,
                    copy: t`Learn how to make webpages with HTML tags and CSS, finishing up by making your very own greeting card.`,
                    link: {
                        href: "/computing/hour-of-code/hour-of-code-lessons/hour-of-html/v/making-webpages-intro",
                        copy: t`Start creating webpages`,
                    },
                    illustration: "HourOfWebpages",
                },
                {
                    kind: "ListItem",
                    headline: t`Hour of Databases`,
                    copy: t`Like playing with data? Learn how to manipulate data in a database and make your own custom store.`,
                    link: {
                        href: "/computing/hour-of-code/hour-of-code-lessons/hour-of-sql/v/welcome-to-sql",
                        copy: t`Start creating databases`,
                    },
                    illustration: "HourOfDatabases",
                },
            ],
        },
        {
            kind: "Testimonial",
            quote: t`“I have to admit that I was nervous about trying it, since I know nothing about coding...All of my students from grades 6-12 participated in the Hour of Code...Everyone loved it.”`,
            attribution: t`Mrs. Cassidy`,
            meta: t`6-12 Science Teacher`,
            backgroundIllustration: "BackgroundBlob",
        },
        {
            kind: "InfoBlock",
            headline: t`Hour of code in your classroom`,
            copy: t`The Hour of Code is a global movement by Computer Science Education Week and Code.org reaching tens of millions of students in 180+ countries through a one-hour introduction to computer science and computer programming. Learn about the simple steps you can take to prepare your class for an Hour of Code.`,
            illustration: "InClassroom",
            cta: {
                type: "button",
                copy: t`Teachers, start here`,
                url: "/computing/hour-of-code/hour-of-code-resources/hour-of-code-for-teachers/a/using-hour-of-code-in-your-classroom",
            },
        },
    );

    modules.push({
        kind: "InfoBlockGroup",
        headline: t`Go beyond the hour`,
        startingAlignment: "right",
        backgroundIllustration: "BackgroundBlob",
        blocks: [
            {
                kind: "InfoBlock",
                headline: t`More JavaScript: Drawing and Animation`,
                copy: t`Learn how to use variables, make animations, store your data in arrays and objects, group your code into functions, and more!`,
                illustration: "MoreJS",
                cta: {
                    type: "link",
                    url: "/computing/computer-programming/programming",
                    copy: t`Learn more`,
                },
            },
            {
                kind: "InfoBlock",
                headline: t`More HTML/CSS: Making Webpages`,
                copy: t`Learn more tags like links and tables, then learn more powerful css selectors and properties to completely re-style your page.`,
                illustration: "MoreHTML",
                cta: {
                    type: "link",
                    url: "/computing/computer-programming/html-css",
                    copy: t`Learn more`,
                },
            },
            {
                kind: "InfoBlock",
                headline: t`More SQL: Querying and managing data`,
                copy: t`Learn more advanced ways to filter data, plus how to update data and combine data from multiple tables.`,
                illustration: "MoreSQL",
                cta: {
                    type: "link",
                    url: "/computing/computer-programming/sql",
                    copy: t`Learn more`,
                },
            },
        ],
    });

    modules.push({
        kind: "LastChance",
        headline: t`Start your hour today`,
        ctas: [
            {
                type: "button",
                copy: t`Start coding now`,
                href: "/computing/hour-of-code/hour-of-drawing-code/v/welcome-hour-of-code",
            },
            {
                type: "button",
                copy: t`Teachers, start here`,
                href: "/computing/hour-of-code/hour-of-code-resources/hour-of-code-for-teachers/a/using-hour-of-code-in-your-classroom",
            },
            {
                type: "button",
                copy: t`Parents, start here`,
                href: "/computing/hour-of-code/hour-of-code-resources/hour-of-code-for-teachers/a/using-hour-of-code-with-your-child",
            },
        ],
    });

    return modules;
};

type Props = Record<any, any>;

class HourOfCodePage extends React.Component<Props> {
    render(): React.ReactNode {
        const modules = getModules();
        const filteredModules = modules.filter((module) => {
            return hourOfCodeModules.hasOwnProperty(module.kind);
        });

        return (
            <PageRoot
                settings={{
                    title: t`Hour of Code on Khan Academy`,
                    description: t`Anyone can learn to code! Spend an hour on Khan Academy learning to code, with our tutorials in JS, HTML, or SQL.`,
                    headerBackgroundColor: "white",
                    imageUrl: staticUrl(
                        "/images/hour_of_code/try_an_hour_2019.png",
                    ),
                }}
            >
                <View style={styles.landingPage}>
                    <Media>
                        <ModuleList
                            {...this.props}
                            domain="default"
                            modules={filteredModules}
                            moduleComponents={hourOfCodeModules}
                        />
                    </Media>
                </View>
            </PageRoot>
        );
    }
}

export default withStrings(HourOfCodePage);

const styles = StyleSheet.create({
    landingPage: {
        // The blobs and collages intentionally render beyond the width of most displays.
        // Prevent that from expanding the screen width.
        overflowX: "hidden",
    },
});
